import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import axios from 'axios';
import logo from '../logo.png';
import CreatedBy from '../components/CreatedBy';

function formatDate(iso) {
  return new Date(iso).toLocaleString('ja-JP', {
    year: 'numeric', month: 'long', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function CompletePage() {
  const { fileId } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [copiedPw, setCopiedPw] = useState(false);
  const [copiedAll, setCopiedAll] = useState(false);
  const [info, setInfo] = useState(null);

  // リロード等でlocation.stateが消えた場合、APIからファイル名と期限を復元する
  // （パスワードはサーバーにハッシュしか残らないため再表示できない）
  useEffect(() => {
    if (state) return;
    axios.get(`/api/files/info.php?fileId=${fileId}`)
      .then((res) => setInfo(res.data))
      .catch(() => {});
  }, [state, fileId]);

  const fileName    = state?.fileName  || info?.originalName;
  const expiresAt   = state?.expiresAt || info?.expiresAt;
  const hasPassword = state ? Boolean(state.password) : Boolean(info?.hasPassword);

  const downloadUrl = `${window.location.origin}/download/${fileId}`;

  const copy = async (text, setter) => {
    try {
      await navigator.clipboard.writeText(text);
      setter(true);
      setTimeout(() => setter(false), 2000);
    } catch {}
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4" style={{ background: '#F5F2EC' }}>
      <CreatedBy />
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <a href="/" className="inline-block" style={{ cursor: 'pointer' }}>
            <img src={logo} alt="and to" style={{ width: '120px', display: 'block' }} />
            <p className="text-xs uppercase text-center" style={{ color: '#6E6A62', width: '120px', marginTop: '8px', letterSpacing: '0.2em', whiteSpace: 'nowrap' }}>
              FILE TRANSFER
            </p>
          </a>
        </div>

        <div
          className="rounded-2xl p-8"
          style={{
            background: '#FDFAF5',
            border: '1px solid #E2DDD4',
            boxShadow: '0 4px 24px rgba(45,42,36,0.07)',
          }}
        >
          {/* Success */}
          <div className="flex items-center justify-center w-14 h-14 rounded-full mx-auto mb-4"
            style={{ background: '#E8F0E9', border: '1.5px solid #6B8F71' }}>
            <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: '#6B8F71' }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
          </div>

          <h2 className="text-lg font-bold text-center mb-1" style={{ color: '#2D2A24' }}>アップロード完了</h2>
          {fileName && (
            <p className="text-xs text-center mb-6 break-all" style={{ color: '#6E6A62' }}>{fileName}</p>
          )}

          {/* Download URL */}
          <div className="mb-4">
            <label className="text-xs font-semibold uppercase tracking-widest mb-2 block" style={{ color: '#6E6A62' }}>
              Download URL
            </label>
            <div className="flex gap-2">
              <input
                readOnly
                value={downloadUrl}
                className="flex-1 rounded-lg px-3 py-2 text-xs focus:outline-none"
                style={{ background: '#F5F2EC', border: '1px solid #E2DDD4', color: '#2D2A24' }}
              />
              <button
                onClick={() => copy(downloadUrl, setCopied)}
                className="px-4 py-2 rounded-lg text-xs font-semibold transition-all duration-200"
                style={copied
                  ? { background: '#E8F0E9', color: '#6B8F71', border: '1px solid #6B8F71' }
                  : { background: '#2D2A24', color: '#F5F2EC' }
                }
              >
                {copied ? 'コピー済' : 'コピー'}
              </button>
            </div>
          </div>

          {/* Password */}
          {state?.password && (
            <div className="mb-4">
              <label className="text-xs font-semibold uppercase tracking-widest mb-2 block" style={{ color: '#6E6A62' }}>
                Password
              </label>
              <div className="flex gap-2">
                <input
                  readOnly
                  value={state.password}
                  type="text"
                  className="flex-1 rounded-lg px-3 py-2 text-xs font-mono focus:outline-none"
                  style={{ background: '#F5E6E0', border: '1px solid #E8C9BC', color: '#C8694A' }}
                />
                <button
                  onClick={() => copy(state.password, setCopiedPw)}
                  className="px-4 py-2 rounded-lg text-xs font-semibold transition-all duration-200"
                  style={copiedPw
                    ? { background: '#E8F0E9', color: '#6B8F71', border: '1px solid #6B8F71' }
                    : { background: '#F5E6E0', color: '#C8694A', border: '1px solid #E8C9BC' }
                  }
                >
                  {copiedPw ? 'コピー済' : 'コピー'}
                </button>
              </div>
              <p className="text-xs mt-1" style={{ color: '#8C8880' }}>このページを離れると再表示できません。必ず控えてください</p>
            </div>
          )}

          {/* リロード等でパスワードが復元できない場合の注意 */}
          {hasPassword && !state?.password && (
            <div className="mb-4 rounded-lg px-3 py-2 text-xs"
              style={{ background: '#F5E6E0', border: '1px solid #E8C9BC', color: '#C8694A' }}>
              このファイルはパスワード保護されています。パスワードは再表示できないため、アップロード時の控えをご確認ください。
            </div>
          )}

          {/* Copy All */}
          {state?.password && (
            <button
              onClick={() => copy(`ダウンロードURL：${downloadUrl}\nパスワード：${state.password}`, setCopiedAll)}
              className="w-full py-2.5 rounded-xl text-xs font-semibold tracking-wide transition-all duration-200 mt-1"
              style={copiedAll
                ? { background: '#E8F0E9', color: '#6B8F71', border: '1px solid #6B8F71' }
                : { background: '#F5F2EC', color: '#6E6A62', border: '1px solid #E2DDD4' }
              }
            >
              {copiedAll ? 'コピー済' : 'URLとパスワードをまとめてコピー'}
            </button>
          )}

          {/* Expiry */}
          {expiresAt && (
            <p className="text-xs mt-3 text-center" style={{ color: '#6E6A62' }}>
              有効期限：<span style={{ color: '#2D2A24' }} className="font-medium">{formatDate(expiresAt)}</span>
            </p>
          )}

          <button
            onClick={() => navigate('/')}
            className="mt-6 w-full py-3 rounded-xl font-semibold text-sm tracking-wide transition-all duration-200"
            style={{ background: 'transparent', border: '1px solid #E2DDD4', color: '#6E6A62' }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#EDEBE4'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            新しいファイルを送る
          </button>
        </div>
      </div>
    </div>
  );
}
